/**
 * CustomerListPage — Issue #345 admin operational table rollout.
 *
 * Backed by the existing `useCustomerList` hook (real GET
 * /api/v1/crm/customers) and the shared `AdminDataTable` so the page gains
 * advanced search, column visibility, loading/empty/error states and a
 * permission-aware row action menu. Customer records are PHI-adjacent so the
 * destructive action is `归档`, implemented as the `lost` sales-stage
 * transition on the existing customer state-machine endpoint (no physical
 * deletion).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import { toast } from 'sonner';
import * as Dialog from '@radix-ui/react-dialog';
import { Archive, Eye, Pencil } from 'lucide-react';
import {
  useCustomerList,
  useCustomerStageTransition,
  CUSTOMER_STATUS_LABELS,
  CUSTOMER_STATUS_LIST,
  INTENT_LEVEL_LABELS,
  type CustomerDto,
  type CustomerStatus,
} from '@/hooks/useCrm';
import { useAuth } from '@/hooks/useAuth';
import AccessDenied from '@/components/AccessDenied';
import { CRM_PERMISSIONS, hasPermission } from '@/lib/crmPermissions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AdminDataTable,
  type AdminDataTableAction,
  type AdminDataTableColumn,
} from '@/components/business/AdminDataTable';

const ARCHIVED_STATUS: CustomerStatus = 'lost' as CustomerStatus;
const TERMINAL_STATUSES: ReadonlySet<CustomerStatus> = new Set([
  'repurchased',
  'referred',
  'lost',
] as CustomerStatus[]);

interface ArchiveTarget {
  readonly customer: CustomerDto;
  readonly sessionKey: number;
}

export default function CustomerListPage() {
  const [, navigate] = useLocation();
  const { permissions } = useAuth();
  const { loading, error, fetch } = useCustomerList();
  const { loading: archiving, transition } = useCustomerStageTransition();
  const [customers, setCustomers] = useState<readonly CustomerDto[]>([]);
  const [total, setTotal] = useState(0);
  // Issue #345 audit fix: only filters the backend supports end-to-end are
  // exposed in the toolbar. A previous version also held a `name` text input
  // which silently filtered the current page in memory — that misled users
  // when matches existed on later pages while the server `total` still
  // counted everything. Backend text search for customers is tracked as a
  // follow-up and will return alongside this field; until then 阶段 is the
  // only server-backed filter we render.
  const [searchValues, setSearchValues] = useState<Record<string, string>>({
    status: '',
  });
  const [page, setPage] = useState(1);
  const limit = 20;
  const [archiveTarget, setArchiveTarget] = useState<ArchiveTarget | null>(null);
  const archiveSessionRef = useRef(0);

  const canRead = hasPermission(permissions, CRM_PERMISSIONS.CUSTOMER_READ);
  const canCreate = hasPermission(permissions, CRM_PERMISSIONS.CUSTOMER_CREATE);
  const canUpdate = hasPermission(permissions, CRM_PERMISSIONS.CUSTOMER_UPDATE);

  const statusFilter = (searchValues['status'] ?? '') as CustomerStatus | '';

  const refresh = useCallback(async () => {
    const result = await fetch({
      page,
      limit,
      status: statusFilter !== '' ? statusFilter : undefined,
    });
    if (result) {
      setCustomers(result.data);
      setTotal(result.total);
    }
  }, [fetch, page, statusFilter]);

  useEffect(() => {
    if (!canRead) return;
    void refresh();
  }, [canRead, refresh]);

  useEffect(() => {
    if (error) toast.error(`加载客户列表失败：${error.message}`);
  }, [error]);

  // Issue #345 audit fix: no client-side substring filter on top of a
  // server-paginated list — that misled users when matches existed on later
  // pages while the server `total` already counted them. Once a backend
  // `search`/`q` parameter ships, the toolbar field will be re-added and the
  // fetch arguments extended.
  const visibleRows = customers;

  const columns: ReadonlyArray<AdminDataTableColumn<CustomerDto>> = useMemo(
    () => [
      {
        key: 'name',
        header: '姓名',
        alwaysVisible: true,
        render: (c) => <span className="font-medium">{c.name}</span>,
      },
      {
        key: 'status',
        header: '阶段',
        render: (c) => (
          <span className="rounded-full bg-muted px-2 py-0.5 text-xs">
            {CUSTOMER_STATUS_LABELS[c.status]}
          </span>
        ),
      },
      {
        key: 'intentLevel',
        header: '意向',
        render: (c) =>
          c.intentLevel ? INTENT_LEVEL_LABELS[c.intentLevel] : '—',
      },
      {
        key: 'createdAt',
        header: '创建时间',
        render: (c) => new Date(c.createdAt).toLocaleDateString('zh-CN'),
      },
    ],
    [],
  );

  const rowActions: ReadonlyArray<AdminDataTableAction<CustomerDto>> = useMemo(
    () => [
      {
        key: 'view',
        label: '查看',
        icon: <Eye className="h-3.5 w-3.5" />,
        onSelect: (c) => navigate(`/crm/customers/${c.id}`),
      },
      {
        key: 'edit',
        label: '编辑',
        icon: <Pencil className="h-3.5 w-3.5" />,
        visible: () => canUpdate,
        onSelect: (c) => navigate(`/crm/customers/${c.id}`),
      },
      {
        key: 'archive',
        label: '归档（标记流失）',
        icon: <Archive className="h-3.5 w-3.5" />,
        destructive: true,
        visible: (c) => canUpdate && !TERMINAL_STATUSES.has(c.status),
        disabled: () => archiving,
        // Issue #345 audit fix: 流失（lost）是需求文档要求记录原因/复核结论的
        // 终态。先打开归档对话框收集 reason（必填），再调用现有
        // POST /api/v1/crm/customers/{id}/status 的 reason 字段。客户档案不可
        // 物理删除（AI GUIDE §6）；归档由后端 transition 校验状态机和乐观锁。
        onSelect: (c) => {
          archiveSessionRef.current += 1;
          setArchiveTarget({
            customer: c,
            sessionKey: archiveSessionRef.current,
          });
        },
      },
    ],
    [archiving, canUpdate, navigate],
  );

  if (!canRead) return <AccessDenied resource="客户管理" />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">客户列表</h2>
        {canCreate && (
          <Button
            type="button"
            onClick={() => navigate('/crm/customers/create')}
          >
            新建客户
          </Button>
        )}
      </div>

      <AdminDataTable<CustomerDto>
        testIdPrefix="crm-customers"
        columns={columns}
        rows={visibleRows}
        isLoading={loading}
        error={error}
        onRetry={refresh}
        emptyMessage="暂无客户数据"
        filters={{
          fields: [
            {
              kind: 'select',
              name: 'status',
              label: '阶段',
              options: CUSTOMER_STATUS_LIST.map((s) => ({
                value: s,
                label: CUSTOMER_STATUS_LABELS[s],
              })),
            },
          ],
          values: searchValues,
          onSubmit: (next) => {
            setSearchValues(next);
            setPage(1);
          },
          onReset: () => {
            setSearchValues({ status: '' });
            setPage(1);
          },
        }}
        pagination={{
          page,
          pageSize: limit,
          total,
          onPageChange: setPage,
        }}
        rowActions={rowActions}
      />

      {archiveTarget && (
        <ArchiveCustomerDialog
          key={`archive-customer-${archiveTarget.customer.id}-${archiveTarget.sessionKey}`}
          customer={archiveTarget.customer}
          submitting={archiving}
          onCancel={() => setArchiveTarget(null)}
          onSubmit={async (reason) => {
            const next = await transition(archiveTarget.customer.id, {
              nextStatus: ARCHIVED_STATUS,
              expectedVersion: archiveTarget.customer.version,
              reason,
            });
            if (next) {
              toast.success(`客户「${archiveTarget.customer.name}」已归档`);
              setArchiveTarget(null);
              await refresh();
              return true;
            }
            return false;
          }}
        />
      )}
    </div>
  );
}

interface ArchiveCustomerDialogProps {
  readonly customer: CustomerDto;
  readonly submitting: boolean;
  readonly onCancel: () => void;
  readonly onSubmit: (reason: string) => Promise<boolean>;
}

function ArchiveCustomerDialog({
  customer,
  submitting,
  onCancel,
  onSubmit,
}: ArchiveCustomerDialogProps) {
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const trimmed = reason.trim();
  const tooShort = trimmed.length < 4;
  const showError = touched && tooShort;
  return (
    <Dialog.Root
      open
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white p-6 shadow-lg"
          data-testid="crm-customer-archive-dialog"
        >
          <Dialog.Title className="mb-1 text-lg font-semibold">
            归档（标记流失）
          </Dialog.Title>
          <Dialog.Description className="mb-3 text-sm text-gray-500">
            将客户「{customer.name}」标记为流失（lost）。该操作不会物理删除任何
            记录；需求文档要求记录流失原因，请填写后提交。
          </Dialog.Description>
          <div className="space-y-1">
            <Label htmlFor="crm-customer-archive-reason">流失原因（必填）</Label>
            <Input
              id="crm-customer-archive-reason"
              data-testid="crm-customer-archive-reason"
              autoComplete="off"
              placeholder="如 客户反复爽约，长期失联"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              onBlur={() => setTouched(true)}
            />
            {showError && (
              <p
                className="text-xs text-red-600"
                data-testid="crm-customer-archive-reason-error"
              >
                请填写至少 4 个字的流失原因，便于复核
              </p>
            )}
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              data-testid="crm-customer-archive-cancel"
              onClick={onCancel}
            >
              取消
            </Button>
            <Button
              type="button"
              disabled={submitting || tooShort}
              data-testid="crm-customer-archive-submit"
              onClick={async () => {
                setTouched(true);
                if (tooShort) return;
                await onSubmit(trimmed);
              }}
            >
              {submitting ? '提交中…' : '确认归档'}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
